<template>
    <div class="bg-white pb-3 pt-5 flex flex-col md:py-7">
        <div class="container">
            <div>
                <BtnCta
                    variant="tertiary"
                    left-icon="chevron-left"
                    :label="goBackLabel"
                    :href="goBackUrl"
                    size="sm"
                    class="mb-3"
                    compact
                />
                <div class="flex flex-col justify-between mb-3 md:flex-row md:flex-wrap">
                    <div class="flex flex-row order-last md:order-none">
                        <BtnRateFlashcard
                            v-model:user-vote="flashcardSet.user_star_vote"
                            v-model:avg-score="flashcardSet.avg_star_score"
                            :ratings-count="flashcardSet.ratings_count"
                            :set-id="flashcardSet.id"
                            variant="outlined"
                            :disabled="isOwner"
                            size="sm"
                            class="mr-3"
                            @update:ratings-count="updateRating($event)"
                        />
                        <BtnFollowFlashcard
                            variant="outlined"
                            size="sm"
                            class="mr-3"
                            :set-id="flashcardSet.id"
                            :active="user?.set_data?.has_followed"
                            :disabled="isOwner"
                            @update:active="user.set_data.has_followed = $event"
                        />
                        <Btn
                            v-if="screen.isSM"
                            variant="outlined"
                            size="sm"
                            icon="info"
                            class="mr-3"
                            @click="openInfoModal = true"
                        />
                        <BtnMenuShare
                            :share-title="
                                $t('share.title', {
                                    name: flashcardSet.title,
                                })
                            "
                            :share-url="shareUrl"
                            variant="outlined"
                            size="sm"
                        />
                        <BtnMenu
                            v-if="user"
                            variant="outlined"
                            size="sm"
                            left-icon="more"
                            :options="options"
                            :tooltip-text="$t('common:tooltip.more')"
                            class="ml-3 protected-verified"
                        />
                    </div>

                    <div class="flex justify-start mb-3 md:mb-0 md:mt-3 font-display-5 w-full">
                        <h1 class="font-display-3 lg:font-display-2">
                            <i
                                v-if="isOwner"
                                :class="isPublic ? 'icon-globe' : 'icon-lock'"
                            ></i>
                            {{ flashcardSet.title }}
                        </h1>
                    </div>
                </div>

                <ModalSheet
                    v-if="screen.maxSM"
                    :open="openInfoModal"
                    @closed="openInfoModal = false"
                >
                    <FlashcardShowInfo
                        :flashcard-set="flashcardSet"
                        :user="user"
                    />
                </ModalSheet>

                <FlashcardShowInfo
                    v-else
                    :flashcard-set="flashcardSet"
                    :user="user"
                />

                <div class="w-full">
                    <FlashCardShowStatistics
                        v-if="isAuth"
                        :flashcard-set="flashcardSet"
                        user-has-visited
                        :user="user"
                    ></FlashCardShowStatistics>

                    <!-- IMPORTANT: With SDFE-642 we introduced permament redirects which made the page inaccessible to some users.
                        With `?` we can bust the browser cache and force the page to load the original URL. -->
                    <BtnCta
                        :href="`${flashcardSet.link_study_page}?`"
                        :label="$t('flashcards:detailsCardList.study')"
                        class="protected-auth float-right mt-4 w-full md:w-auto"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import route from '@router/route';
import ButtonBookmark from '@components/ButtonBookmark';
import BtnFollowFlashcard from '@components/BtnFollowFlashcard';
import FlashCardShowStatistics from '@components/Flashcard/FlashCardShowStatistics.vue';
import FlashcardShowInfo from '@components/Flashcard/FlashcardShowInfo.vue';
import ModalSheet from '@components/ModalSheet';
import stripWhiteSpaceSpecialCharacters from '@/helpers/stripWhiteSpaceSpecialCharacters';
import BtnMenuShare from '@components/BtnMenuShare/BtnMenuShare.vue';
import BtnRateFlashcard from '@/components/BtnRateFlashcard';

export default {
    components: {
        BtnFollowFlashcard,
        BtnRateFlashcard,
        BtnMenuShare,
        ButtonBookmark,
        FlashCardShowStatistics,
        FlashcardShowInfo,
        ModalSheet,
    },
    i18nOptions: {
        namespaces: ['flashcards'],
    },
    props: {
        flashcardSet: { type: Object, required: true },
        user: { type: Object, required: false, default: null },
    },
    data() {
        return {
            isOwner: sdWindow.flashcards.isOwner,
            isAdmin: sdWindow.flashcards.isAdmin,
            openInfoModal: false,
            progress: false,
            isPublic: this.flashcardSet.is_public,
        };
    },
    computed: {
        ...mapGetters('ui', ['screen']),
        isAuth() {
            return !!sdWindow.flashcards.user;
        },
        hasCourse() {
            return !!this.flashcardSet.course;
        },
        shareUrl() {
            return `${this.flashcardSet.link}${this.isAuth ? `?ref=${this.user.userid}` : ''}`;
        },
        goBackUrl() {
            return this.hasCourse
                ? `${sdWindow.flashcards.set.course.link}#flashcards`
                : sdWindow.flashcards.goBackLink;
        },
        goBackLabel() {
            return this.hasCourse
                ? this.$t('flashcardShowHeader.btn_back_course')
                : this.$t('flashcardShowHeader.btn_back');
        },
        options() {
            const options = [
                ...[
                    this.screen.minMD
                        ? {
                              label: this.$t('flashcards:flashcardShowHeader.menu.label_edit'),
                              icon: 'edit',
                              href: this.flashcardSet.link_edit_page,
                              visible: this.isOwner,
                          }
                        : {},
                ],
                {
                    label: this.$t('flashcards:flashcardShowHeader.menu.label_report'),
                    icon: 'flag',
                    onClick: this.reportFlashcard,
                    visible: !this.isOwner,
                },
                {
                    label: this.$t('flashcardShowHeader.menu.delete'),
                    icon: 'trash',
                    onClick: this.deleteFlashcard,
                    visible: this.isOwner || this.isAdmin,
                },
            ];
            return options.filter((items) => items.visible);
        },
    },
    methods: {
        route,
        reportFlashcard() {
            this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalReportFlashcard')),
                {
                    flashcardSetId: this.flashcardSet.id,
                },
            );
        },
        stripWhiteSpaceSpecialCharacters,
        async deleteFlashcard() {
            if (this.progress) return;
            this.progress = true;

            const confirmed = await this.$modal.confirmDanger({
                title: this.$t('deleteConfirmModal.title'),
                text: this.$t(`deleteConfirmModal.subtitle${this.isPublic ? '' : '_private'}`),
                submitText: this.$t('deleteConfirmModal.submitText'),
            });

            if (!confirmed) {
                this.progress = false;
                return;
            }

            this.$http
                .post(
                    route('flashcards.sets.delete', {
                        id: this.flashcardSet.id,
                    }),
                )
                .then((response) => {
                    if (response.data.success) {
                        this.$store.dispatch('flashMessages/showSuccess', {
                            content: this.$t('deleteConfirmModal.success'),
                            progress: { time: 3000, showProgress: false },
                        });

                        setTimeout(() => {
                            window.location.href = route('profile.study-material.list', { type: 'flashcards' });
                        }, 1500);
                    } else {
                        this.$store.dispatch('flashMessages/showError', {
                            content: this.$t('deleteConfirmModal.error'),
                            progress: { time: 3000, showProgress: false },
                        });
                    }
                })
                .finally(() => {
                    this.progress = false;
                });
        },
        updateRating(newRatingCount) {
            this.flashcardSet.ratings_count = newRatingCount;
        },
    },
};
</script>
